import { engineComponent, StringUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { HtmlContainerElement } from "../HTMLComponents/Elements/HtmlContainerElement";
import EventEmitter = PIXI.utils.EventEmitter;

@engineComponent(ComponentTypes.HtmlElement)
export class HtmlTouchContainerElement extends HtmlContainerElement {
    //******************************************************************************************************************
    //  PUBLIC EVENTS
    //******************************************************************************************************************

    public static readonly evSwipeUp: string = "eventSwipeUp";

    public static readonly evMoveUp: string = "eventMoveUp";

    public static readonly evSwipeDown: string = "eventSwipeDown";

    public static readonly evMoveDown: string = "eventMoveDown";

    public static readonly evSwipeLeft: string = "eventSwipeLeft";

    public static readonly evMoveLeft: string = "eventMoveLeft";

    public static readonly evSwipeRight: string = "eventSwipeRight";

    public static readonly evMoveRight: string = "eventMoveRight";

    public static readonly evTouchStart: string = "eventTouchStart";

    public static readonly evTouchMove: string = "eventTouchMove";

    public static readonly evTouchEnd: string = "eventTouchEnd";

    public static readonly evTap: string = "eventTap";

    //******************************************************************************************************************
    //  SETTINGS
    //******************************************************************************************************************

    private static SwipeThreshold: number = 30;

    private static MoveThreshold: number = 30;

    private static MoveThresholdMax: number = 30;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private touchStartX: number;
    private touchStartY: number;

    private touchMoveProgressX: number;
    private touchMoveProgressY: number;

    private emitter: EventEmitter;

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    constructor() {
        super();

        this.emitter = new EventEmitter();
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    create(node: Element): void {
        super.create(node);

        this.instance.addEventListener("touchstart", this.onTouchStart.bind(this));
        this.instance.addEventListener("touchmove", this.onTouchMove.bind(this));
        this.instance.addEventListener("touchend", this.onTouchEnd.bind(this));
    }

    //******************************************************************************************************************
    //  CALCULATE
    //******************************************************************************************************************

    private onTouchStart(e: TouchEvent): void {
        if (e != null) {
            e.preventDefault();
            e.stopImmediatePropagation();

            this.touchStartX = e.touches[0].pageX;
            this.touchStartY = e.touches[0].pageY;

            this.touchMoveProgressX = this.touchStartX;
            this.touchMoveProgressY = this.touchStartY;
        }
    }

    private onTouchMove(e: TouchEvent): void {
        if (e != null) {
            e.stopImmediatePropagation();

            let landscape = this.instance.clientWidth > this.instance.clientHeight;
            HtmlTouchContainerElement.MoveThreshold = landscape
                ? this.instance.clientHeight / 4
                : this.instance.clientWidth / 4;
            HtmlTouchContainerElement.MoveThreshold =
                HtmlTouchContainerElement.MoveThreshold > HtmlTouchContainerElement.MoveThresholdMax
                    ? HtmlTouchContainerElement.MoveThresholdMax
                    : HtmlTouchContainerElement.MoveThreshold;

            const currentX = e.changedTouches[0].pageX;
            const currentY = e.changedTouches[0].pageY;

            if (this.emitter != null) {
                const diffX = this.touchMoveProgressX - currentX;
                const diffY = this.touchMoveProgressY - currentY;

                if (Math.abs(diffX) > Math.abs(diffY)) {
                    if (Math.abs(diffX) > HtmlTouchContainerElement.MoveThreshold) {
                        e.preventDefault();
                        this.touchMoveProgressX = currentX;
                        if (diffX > 0) {
                            this.emitter.emit(HtmlTouchContainerElement.evMoveLeft);
                            // console.log("Move " + HtmlTouchContainerElement.evMoveLeft);
                        } else {
                            this.emitter.emit(HtmlTouchContainerElement.evMoveRight);
                            // console.log("Move " + HtmlTouchContainerElement.evMoveRight);
                        }
                    }
                } else {
                    if (Math.abs(diffY) > HtmlTouchContainerElement.MoveThreshold) {
                        e.preventDefault();
                        this.touchMoveProgressY = currentY;
                        if (diffY > 0) {
                            this.emitter.emit(HtmlTouchContainerElement.evMoveUp);
                            // console.log("Move " + HtmlTouchContainerElement.evMoveUp);
                        } else {
                            this.emitter.emit(HtmlTouchContainerElement.evMoveDown);
                            // console.log("Move " + HtmlTouchContainerElement.evMoveDown);
                        }
                    }
                }
            }
        }
    }

    private onTouchEnd(e: TouchEvent): void {
        if (e != null) {
            e.stopImmediatePropagation();

            const currentX = e.changedTouches[0].pageX;
            const currentY = e.changedTouches[0].pageY;

            if (this.emitter != null) {
                const diffX = Math.abs(this.touchStartX - currentX);
                const diffY = Math.abs(this.touchStartY - currentY);
                if (diffX != 0 || diffY != 0) {
                    if (Math.abs(diffX) > Math.abs(diffY)) {
                        if (Math.abs(diffX) > HtmlTouchContainerElement.SwipeThreshold) {
                            e.preventDefault();
                            if (diffX > 0) {
                                this.emitter.emit(HtmlTouchContainerElement.evSwipeLeft);
                                // console.log("SWIPE " + HtmlTouchContainerElement.evSwipeLeft);
                            } else {
                                this.emitter.emit(HtmlTouchContainerElement.evSwipeRight);
                                // console.log("SWIPE " + HtmlTouchContainerElement.evSwipeRight);
                            }
                        }
                    } else {
                        if (Math.abs(diffY) > HtmlTouchContainerElement.SwipeThreshold) {
                            e.preventDefault();
                            if (diffY > 0) {
                                this.emitter.emit(HtmlTouchContainerElement.evSwipeUp);
                                // console.log("SWIPE " + HtmlTouchContainerElement.evSwipeUp);
                            } else {
                                this.emitter.emit(HtmlTouchContainerElement.evSwipeDown);
                                // console.log("SWIPE " + HtmlTouchContainerElement.evSwipeDown);
                            }
                        }
                    }
                } else {
                    this.emitter.emit(HtmlTouchContainerElement.evTap);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  PUBLIC HANDLERS
    //******************************************************************************************************************

    public on(event: string, callbackFnc: Function): void {
        if (this.emitter != null) {
            this.emitter.addListener(event, callbackFnc);
        }
    }

    public unbind(event: string, callbackFnc: Function): void {
        if (this.emitter != null) {
            this.emitter.removeListener(event, callbackFnc);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/HtmlTouchContainerElement.ts
 **/